import useFilePermissions from "~/hooks/useFilePermissions";

/**
 * Determines whether a user has read or write permissions for a specific file or directory.
 *
 * Handles various permission scenarios:
 * - Wildcard permissions (paths ending with "/*")
 * - Exact path matches
 * - Parent directory permissions (read access to parent paths)
 *
 * @param {string} key - The file or directory path to check permissions for
 * @param {string} permission - The permission type to check ("read" or "write")
 * @returns {boolean|null} True if permitted, false if denied, null if loading
 *
 * @example
 * const canWrite = useFileAccess("raw/my-folder", "write");
 */
const useFileAccess = (key = "", permission: "read" | "write") => {
  const { data: permissions = {}, isLoading } = useFilePermissions();

  if (isLoading) {
    return null;
  }

  const normalizedKey = normalizeKey(key);

  return Object.entries(permissions).some(([path, value]) => {
    if (!value.includes(permission)) {
      return false;
    }
    if (grantCovers(path, normalizedKey)) {
      return true;
    }
    // The user has access to something nested below `key`, so they need read
    // access to `key` in order to navigate down to it. Only grant read.
    return permission === "read" && grantIsBeneath(path, normalizedKey);
  });
};

export type FileAccessLevel = "write" | "read" | "browse" | "none";

/**
 * Returns the caller's strongest access on `key` in one pass over the grants, or null while loading.
 * "browse" means read only through a grant nested beneath `key` (see useFileAccess's parent-directory rule).
 */
export const useFileAccessLevel = (key = ""): FileAccessLevel | null => {
  const { data: permissions = {}, isLoading } = useFilePermissions();

  if (isLoading) {
    return null;
  }

  return getFileAccessLevel(permissions, key);
};

/** Returns the caller's strongest access on `key` under `permissions`, the `/permissions` map. */
export function getFileAccessLevel(
  permissions: Record<string, string[]>,
  key: string
): FileAccessLevel {
  const normalizedKey = normalizeKey(key);
  let level: FileAccessLevel = "none";
  for (const [path, value] of Object.entries(permissions)) {
    if (grantCovers(path, normalizedKey)) {
      if (value.includes("write")) {
        return "write";
      }
      if (value.includes("read")) {
        level = "read";
      }
    } else if (level === "none" && value.includes("read") && grantIsBeneath(path, normalizedKey)) {
      level = "browse";
    }
  }
  return level;
}

// Grant paths never carry a trailing slash, but directory keys sometimes do
// (e.g. "foo/bar/"). Normalize so the segment-boundary checks line up.
function normalizeKey(key: string) {
  return key.endsWith("/") ? key.slice(0, -1) : key;
}

// An exact grant, or a wildcard grant (e.g. "foo/*") on the key or an ancestor. Match on
// path-segment boundaries so a grant on "foo/*" does not leak to a sibling like "foo-bar".
function grantCovers(path: string, normalizedKey: string) {
  // Folder grants are always "/*" globs in practice
  if (path === normalizedKey) {
    return true;
  }
  if (!path.endsWith("/*")) {
    return false;
  }
  const prefix = path.slice(0, -2);
  return normalizedKey === prefix || normalizedKey.startsWith(`${prefix}/`);
}

// A grant on something nested below the key, on a segment boundary so "foo" is not
// treated as an ancestor of "foo-bar/...".
function grantIsBeneath(path: string, normalizedKey: string) {
  return path.startsWith(`${normalizedKey}/`);
}

export default useFileAccess;
